import { KeyboardEventCode } from '@lemonade-hq/cdk';
import { clsx } from 'clsx';
import type { ForwardedRef } from 'react';
import React, { useCallback, useEffect, useRef } from 'react';
import { useDateSegment } from 'react-aria';
import type { DateFieldState, DateSegment as DateSegmentType } from 'react-stately';
import { Text } from '../../Text/Text';
import * as styles from './DateField.css';

interface DateSegmentProps {
  readonly segment: DateSegmentType;
  readonly state: DateFieldState;
  readonly onSegmentClick?: (event: React.MouseEvent<HTMLParagraphElement>) => void;
  readonly onSegmentKeyPress?: (event: React.KeyboardEvent<HTMLParagraphElement>) => void;
  readonly shouldFocus?: boolean;
}

export const DateSegment: React.FC<DateSegmentProps> = ({
  segment,
  state,
  onSegmentKeyPress,
  onSegmentClick,
  shouldFocus,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const { segmentProps } = useDateSegment(segment, state, ref);
  const { onKeyDown } = segmentProps;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLParagraphElement>): void => {
      if ((e.key as KeyboardEventCode) === KeyboardEventCode.Enter) {
        onSegmentKeyPress?.(e);
      } else {
        onKeyDown?.(e);
      }
    },
    [onKeyDown, onSegmentKeyPress],
  );

  useEffect(() => {
    if (ref.current && shouldFocus) {
      ref.current.focus();
    }
  }, [shouldFocus]);

  return (
    <Text
      type="bodyMedium"
      {...segmentProps}
      as="p"
      className={clsx(styles.segment, {
        [styles.segmentPlaceholder]: segment.isPlaceholder || !segment.isEditable,
      })}
      onClick={onSegmentClick}
      onKeyDown={handleKeyDown}
      ref={ref as ForwardedRef<JSX.IntrinsicElements['p']>}
    >
      {segment.text}
    </Text>
  );
};
